import { useEffect, useState } from "react";
import { Reveal } from "@/components/Reveal";
import { motion } from "framer-motion";
import { Heart, ChatCircle, PaperPlaneTilt, BookmarkSimple, Airplane } from "@phosphor-icons/react";
import { feedApi } from "@/lib/apiClient";
import CommentsDrawer from "@/components/CommentsDrawer";
import { useTranslation } from "react-i18next";

export default function CommunityFeed() {
    const { t } = useTranslation();
    const [posts, setPosts] = useState([]);
    const [commentPost, setCommentPost] = useState(null);
    useEffect(() => {
        feedApi.list().then(setPosts).catch(() => {});
    }, []);

    return (
        <section id="community" className="relative py-32 md:py-40" data-testid="community-feed">
            <div className="max-w-[1400px] mx-auto px-6 md:px-10">
                <Reveal>
                    <div className="grid md:grid-cols-2 gap-8 items-end mb-16">
                        <div>
                            <div className="overline mb-4">{t("communityFeed.overline")}</div>
                            <h2 className="font-serif text-5xl md:text-7xl text-white leading-[0.95] tracking-tighter">
                                {t("communityFeed.title")}
                            </h2>
                        </div>
                        <p className="text-white/60 text-lg leading-relaxed md:pb-3">
                            {t("communityFeed.subtitle")}
                        </p>
                    </div>
                </Reveal>

                {/* masonry */}
                <div className="columns-1 sm:columns-2 lg:columns-3 gap-6 [&>*]:mb-6">
                    {posts.slice(0, 6).map((p, i) => (
                        <Reveal key={p.id} delay={0.03 * i}>
                            <FeedCard post={p} onComment={setCommentPost} />
                        </Reveal>
                    ))}
                </div>
            </div>

            <CommentsDrawer post={commentPost} open={!!commentPost} onClose={() => setCommentPost(null)} />
        </section>
    );
}

function FeedCard({ post, onComment }) {
    const [liked, setLiked] = useState(false);
    const [likes, setLikes] = useState(post.likes || 0);
    const toggle = async () => {
        try {
            const r = await feedApi.like(post.id);
            setLiked(r.liked);
            setLikes((n) => n + (r.liked ? 1 : -1));
        } catch (e) {
            setLiked((v) => !v);
        }
    };

    return (
        <motion.div
            whileHover={{ y: -3 }}
            transition={{ duration: 0.5 }}
            className="break-inside-avoid rounded-3xl overflow-hidden card-hover"
            style={{ background: "var(--navy-deep)", border: "1px solid rgba(255,255,255,0.06)" }}
            data-testid={`post-${post.id}`}
        >
            <div className="relative">
                <img src={post.image_url} alt={post.location} className="w-full object-cover" style={{ maxHeight: 520 }} />
                <div className="absolute top-4 left-4 glass px-3 py-1.5 rounded-full text-xs flex items-center gap-2">
                    <Airplane size={12} color="#D4AF37" weight="fill" />
                    <span className="text-white/90 uppercase tracking-[0.15em]">{post.country}</span>
                </div>
            </div>
            <div className="p-5">
                <div className="flex items-center gap-4 mb-3">
                    <button onClick={toggle} className="flex items-center gap-1.5" data-testid={`post-like-${post.id}`}>
                        <Heart size={20} weight={liked ? "fill" : "regular"} color={liked ? "#D4AF37" : "white"} />
                        <span className="text-sm text-white/80">{likes}</span>
                    </button>
                    <button onClick={() => onComment?.(post)} className="flex items-center gap-1.5" data-testid={`post-comment-${post.id}`}>
                        <ChatCircle size={20} />
                        <span className="text-sm text-white/80">{post.comments || 0}</span>
                    </button>
                    <PaperPlaneTilt size={20} />
                    <BookmarkSimple size={20} className="ml-auto" />
                </div>
                <div className="text-sm">
                    <span className="font-medium" style={{ color: "var(--gold)" }}>{post.user_handle}</span>{" "}
                    <span className="text-white/80">{post.caption}</span>
                </div>
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                    {(post.tags || []).map((t) => (
                        <span key={t} className="text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded-full" style={{ background: "rgba(255,255,255,0.04)", color: "var(--gold)" }}>
                            #{t}
                        </span>
                    ))}
                    <span className="text-white/40 text-xs ml-auto">{post.location}</span>
                </div>
            </div>
        </motion.div>
    );
}
